import dayjs, { type Dayjs } from 'dayjs';
import {
  editDialogInputBackgroundColor,
  EntityEditDialog,
} from '@/features/shared/components/entityDetails/EntityEditDialog';
import {
  EntityEditDateField,
  EntityEditStatusField,
  EntityEditTagsField,
} from '@/features/shared/components/entityDetails/EntityEditFields';
import { getFormLabel } from '@/shared/copy/forms';
import { optionalLabel } from '@/ui/hooks/validation';
import { TextField } from '@/ui/primitives/TextField';
import {
  type ReleaseFormErrors,
  type ReleaseFormHandlers,
  type ReleaseFormValues,
  type StatusOption,
} from './types';
import type { ReactElement } from 'react';

type EditReleaseDialogProps = {
  open: boolean;
  saving?: boolean;
  values: ReleaseFormValues;
  errors?: ReleaseFormErrors;
  statusOptions: StatusOption[];
  showStatusSelect: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: () => void;
  handlers: ReleaseFormHandlers;
};

export const EditReleaseDialog = ({
  open,
  saving = false,
  values,
  errors,
  statusOptions,
  showStatusSelect,
  errorMessage,
  onClose,
  onSave,
  handlers,
}: EditReleaseDialogProps): ReactElement => {
  const plannedForLabel = optionalLabel('Planned For');
  const releasedAtLabel = optionalLabel('Released At');
  const Field = TextField;
  const toDayjs = (value: string): Dayjs | null => (value ? dayjs(value) : null);
  const toDateString = (value: Dayjs | null): string => (value ? value.format('YYYY-MM-DD') : '');

  return (
    <EntityEditDialog
      open={open}
      entityLabel="Release"
      entityName={values.name}
      saving={saving}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      <Field
        label={getFormLabel('name')}
        value={values.name}
        onChange={(event) => {
          handlers.onNameChange(event.target.value);
        }}
        fieldStyle="settings"
        inputBackgroundColor={editDialogInputBackgroundColor}
        required
        autoFocus
        {...(errors?.name ?? {})}
      />
      {showStatusSelect ? (
        <EntityEditStatusField
          value={values.status}
          options={statusOptions}
          onChange={(nextValue) => {
            handlers.onStatusChange?.(nextValue);
          }}
        />
      ) : null}
      <EntityEditDateField
        label={plannedForLabel}
        value={toDayjs(values.plannedFor)}
        onChange={(nextValue) => {
          handlers.onPlannedForChange(toDateString(nextValue));
        }}
      />
      <EntityEditDateField
        label={releasedAtLabel}
        value={toDayjs(values.releasedAt)}
        onChange={(nextValue) => {
          handlers.onReleasedAtChange(toDateString(nextValue));
        }}
      />
      <EntityEditTagsField
        open={open}
        projectId={values.projectId}
        tags={values.tags ?? []}
        helperText="Tags will be applied only to this release."
        onTagsChange={(nextTags) => {
          handlers.onTagsChange?.(nextTags);
        }}
      />
    </EntityEditDialog>
  );
};
